import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PortfolioLink, PortfolioMapPoint, PortfolioMotif } from '../../types/portfolio';
import { MAP_COLORS, truncate } from './portfolioFormat';

/**
 * The semantic map (port of Olisé's SemanticMap): every project of the
 * organization as a dot placed by proximity of meaning (unit coordinates from
 * the backend). « Par motif » draws the motifs as dashed outlines around their
 * members (unreadable ones included); « Par domaine » regroups the readable
 * projects into one pale zone per domain: zones start where their projects sit
 * semantically, are sized by their number of projects and pushed apart until
 * neither discs nor labels overlap, and the projects are spread inside on a
 * sunflower spiral. A project the caller cannot read is a small muted dot:
 * never named, never clickable (and absent from « Par domaine », having no domain).
 *
 * Pure SVG. Geometry is memoised on the size and the data; the timeline only
 * flips each dot's visibility, and dots are memoised so a replay step
 * re-renders the ones whose state changed.
 */

export type MapMode = 'motif' | 'domain';

interface SemanticMapProps {
  points: readonly PortfolioMapPoint[];
  motifs: readonly PortfolioMotif[];
  /** Saved rapprochements, drawn as thin orange lines between their members. */
  links?: readonly PortfolioLink[];
  /** Deposits strictly before this time are shown (timeline cursor); null shows everything. */
  cutoff?: number | null;
  mode: MapMode;
  selectedId?: string | null;
  /** Projects to emphasise (members of the rapprochement picked in the side panel). */
  highlightIds?: ReadonlySet<string>;
  /** Members of a recommended rapprochement (orange dot). */
  suggestedIds?: ReadonlySet<string>;
  onSelect?: (id: string | null) => void;
  /** Minimap: no labels, no clicks, tighter padding. */
  compact?: boolean;
  className?: string;
}

/** Above this many readable dots on screen, titles show on hover only (plus the selection). */
const LABEL_LIMIT = 60;
const LABEL_MAX = 30;
const EASE = 'opacity 350ms ease';
const LABEL_HALO: React.CSSProperties = {
  paintOrder: 'stroke',
  stroke: 'hsl(var(--card))',
  strokeWidth: 3.5,
  strokeLinejoin: 'round',
};

interface Placed {
  key: string;
  point: PortfolioMapPoint;
  x: number;
  y: number;
  time: number;
}

interface Disc {
  key: string;
  label: string;
  cx: number;
  cy: number;
  r: number;
}

/** Smallest centred circle (centroid + farthest member) around a few dots, with a margin. */
function discAround(members: readonly Placed[], margin: number, minR: number): { cx: number; cy: number; r: number } {
  const n = members.length || 1;
  const cx = members.reduce((s, m) => s + m.x, 0) / n;
  const cy = members.reduce((s, m) => s + m.y, 0) / n;
  const far = members.reduce((mx, m) => Math.max(mx, Math.hypot(m.x - cx, m.y - cy)), 0);
  return { cx, cy, r: Math.max(minR, far + margin) };
}

/** Rough width of an uppercase zone label at 11px with letter spacing. */
const labelWidth = (label: string, compact: boolean) => Math.min(label.length, compact ? 22 : 40) * (compact ? 6 : 8.2);

interface DomainLayout {
  discs: Disc[];
  positions: Map<string, { x: number; y: number }>;
}

/**
 * One non-overlapping zone per domain (« Par domaine »). Deterministic: zones are
 * processed in a fixed order and pushed apart by a fixed number of passes.
 */
function layoutDomains(placed: readonly Placed[], w: number, h: number, compact: boolean): DomainLayout {
  const spacing = compact ? 9 : 30;
  const pad = compact ? 6 : 16;
  const labelBand = compact ? 12 : 26;
  const groups = new Map<string, Placed[]>();
  for (const p of placed) {
    const name = p.point.id ? p.point.domain_name?.trim() : '';
    if (!name) continue;
    groups.set(name, [...(groups.get(name) ?? []), p]);
  }
  const zones = [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, members]) => {
      const n = members.length;
      const cx = members.reduce((acc, m) => acc + m.x, 0) / n;
      const cy = members.reduce((acc, m) => acc + m.y, 0) / n;
      // Sunflower spiral: point k at radius c·√k keeps an even spacing.
      const r = (spacing / Math.sqrt(Math.PI)) * Math.sqrt(n) + (compact ? 8 : 26);
      return { name, members, cx, cy, r, lw: labelWidth(name, compact) };
    });

  const left = pad;
  const right = w - pad - (compact ? 0 : 140);
  const clamp = (z: (typeof zones)[number]) => {
    z.cx = Math.min(Math.max(z.cx, left + z.r, left + z.lw / 2), Math.max(left + z.r, right - Math.max(z.r, z.lw / 2)));
    z.cy = Math.min(Math.max(z.cy, pad + z.r + labelBand), Math.max(pad + z.r + labelBand, h - pad - z.r));
  };
  zones.forEach(clamp);
  for (let pass = 0; pass < 200; pass++) {
    let moved = false;
    for (let i = 0; i < zones.length; i++) {
      for (let j = i + 1; j < zones.length; j++) {
        const a = zones[i];
        const b = zones[j];
        let dx = b.cx - a.cx;
        let dy = b.cy - a.cy;
        let d = Math.hypot(dx, dy);
        // Discs plus the label band above each must not touch; labels side by side need their widths.
        const need = Math.max(a.r + b.r + labelBand, Math.abs(dy) < labelBand * 2 ? (a.lw + b.lw) / 2 + 12 : 0);
        if (d >= need) continue;
        if (d < 1e-6) {
          dx = 1;
          dy = 0.37 * (j - i);
          d = Math.hypot(dx, dy);
        }
        const push = (need - d) / 2 + 0.5;
        a.cx -= (dx / d) * push;
        a.cy -= (dy / d) * push;
        b.cx += (dx / d) * push;
        b.cy += (dy / d) * push;
        moved = true;
      }
    }
    zones.forEach(clamp);
    if (!moved) break;
  }

  const positions = new Map<string, { x: number; y: number }>();
  const golden = Math.PI * (3 - Math.sqrt(5));
  const c = spacing / Math.sqrt(Math.PI);
  for (const z of zones) {
    // Keep the semantic order around the zone: sort members by their angle from the centroid.
    const ox = z.members.reduce((acc, m) => acc + m.x, 0) / z.members.length;
    const oy = z.members.reduce((acc, m) => acc + m.y, 0) / z.members.length;
    const ordered = [...z.members].sort(
      (m1, m2) => Math.hypot(m1.x - ox, m1.y - oy) - Math.hypot(m2.x - ox, m2.y - oy) || m1.key.localeCompare(m2.key)
    );
    ordered.forEach((m, k) => {
      const radius = z.members.length === 1 ? 0 : c * Math.sqrt(k + 0.5);
      const angle = k * golden;
      positions.set(m.key, { x: z.cx + radius * Math.cos(angle), y: z.cy + radius * Math.sin(angle) });
    });
  }
  return {
    discs: zones.map((z) => ({ key: z.name, label: z.name, cx: z.cx, cy: z.cy, r: z.r })),
    positions,
  };
}

interface DotProps {
  id: string | null;
  title: string | null;
  meta: string;
  x: number;
  y: number;
  visible: boolean;
  selected: boolean;
  highlighted: boolean;
  qualified: boolean;
  suggested: boolean;
  /** Title always shown (otherwise on hover). */
  labelShown: boolean;
  compact: boolean;
  onSelect?: (id: string | null) => void;
}

const Dot = React.memo(function Dot({
  id,
  title,
  meta,
  x,
  y,
  visible,
  selected,
  highlighted,
  qualified,
  suggested,
  labelShown,
  compact,
  onSelect,
}: DotProps) {
  const anonymous = id === null;
  const clickable = !anonymous && !!onSelect;
  if (anonymous) {
    return (
      <circle
        cx={x}
        cy={y}
        r={compact ? 1.8 : 3}
        style={{ fill: 'hsl(var(--muted-foreground))', fillOpacity: visible ? 0.35 : 0, transition: EASE }}
      />
    );
  }
  const fill = qualified ? MAP_COLORS.qualified : 'hsl(var(--foreground))';
  return (
    <g
      transform={`translate(${x} ${y})`}
      className={clickable ? 'group cursor-pointer' : undefined}
      style={{ opacity: visible ? 1 : 0, pointerEvents: visible && clickable ? 'auto' : 'none', transition: EASE }}
      onClick={
        clickable
          ? (e) => {
              e.stopPropagation();
              onSelect?.(selected ? null : id);
            }
          : undefined
      }
    >
      {!compact && <title>{[title, meta].filter(Boolean).join(' — ')}</title>}
      {(selected || highlighted) && (
        <circle r={11} style={{ fill: selected ? 'hsl(var(--primary))' : MAP_COLORS.suggestion, fillOpacity: 0.16 }} />
      )}
      {selected && <circle r={8} fill="none" style={{ stroke: 'hsl(var(--primary))' }} strokeWidth={1.6} />}
      {clickable && <circle r={10} fill="transparent" />}
      <circle r={compact ? 2.6 : 5} style={{ fill }} />
      {suggested && (
        <circle
          cx={compact ? 2.6 : 4.5}
          cy={compact ? -2.6 : -4.5}
          r={compact ? 1.6 : 2.6}
          style={{ fill: MAP_COLORS.suggestion, stroke: 'hsl(var(--card))' }}
          strokeWidth={1}
        />
      )}
      {!compact && title && (
        <text
          x={12}
          y={4.5}
          fontSize={12}
          fontWeight={selected ? 700 : 400}
          className={labelShown ? undefined : 'opacity-0 transition-opacity group-hover:opacity-100'}
          style={{ fill: selected ? 'hsl(var(--primary))' : 'hsl(var(--foreground))', ...LABEL_HALO }}
        >
          {truncate(title, LABEL_MAX)}
        </text>
      )}
    </g>
  );
});

export const SemanticMap: React.FC<SemanticMapProps> = ({
  points,
  motifs,
  links,
  cutoff = null,
  mode,
  selectedId = null,
  highlightIds,
  suggestedIds,
  onSelect,
  compact = false,
  className,
}) => {
  const { t } = useTranslation();
  const gridId = `portfolio-grid-${useId().replace(/:/g, '')}`;
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ w: Math.round(width), h: Math.round(height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { w, h } = size;

  // Pixel positions: depend on the size and the points only (not on the timeline).
  const placed: Placed[] = useMemo(() => {
    if (!w || !h) return [];
    const padLeft = compact ? 10 : 40;
    const padRight = compact ? 10 : 180;
    const padY = compact ? 10 : 48;
    const innerW = Math.max(1, w - padLeft - padRight);
    const innerH = Math.max(1, h - 2 * padY);
    return points.map((p, i) => ({
      key: p.id ?? `anon-${i}`,
      point: p,
      x: padLeft + Math.min(1, Math.max(0, p.x)) * innerW,
      y: padY + Math.min(1, Math.max(0, p.y)) * innerH,
      time: new Date(p.deposited_at).getTime(),
    }));
  }, [points, w, h, compact]);

  // « Par domaine »: zones computed on every readable project (not only the ones the
  // timeline shows), so the zones stay put while the year replays.
  const domainLayout = useMemo(
    () => (mode === 'domain' && w && h ? layoutDomains(placed, w, h, compact) : null),
    [mode, placed, w, h, compact]
  );
  const shown: Placed[] = useMemo(() => {
    if (!domainLayout) return placed;
    return placed.flatMap((p) => {
      const at = domainLayout.positions.get(p.key);
      return at ? [{ ...p, x: at.x, y: at.y }] : [];
    });
  }, [placed, domainLayout]);

  const isVisible = useCallback((p: Placed) => cutoff === null || p.time < cutoff, [cutoff]);
  const visible = useMemo(() => shown.filter(isVisible), [shown, isVisible]);

  const motifDiscs: Disc[] = useMemo(() => {
    if (mode !== 'motif') return [];
    const byMotif = new Map<string, Placed[]>();
    for (const p of visible) {
      if (!p.point.motif_id) continue;
      const list = byMotif.get(p.point.motif_id) ?? [];
      list.push(p);
      byMotif.set(p.point.motif_id, list);
    }
    return motifs.flatMap((m) => {
      const members = byMotif.get(m.id) ?? [];
      if (members.length < 2) return [];
      return [{ key: m.id, label: m.label, ...discAround(members, compact ? 8 : 22, compact ? 12 : 34) }];
    });
  }, [mode, visible, motifs, compact]);

  const domainDiscs: Disc[] = useMemo(() => {
    if (!domainLayout) return [];
    // A zone appears once one of its projects is on the timeline.
    const present = new Set(visible.map((p) => p.point.domain_name?.trim()));
    return domainLayout.discs.filter((d) => present.has(d.key));
  }, [domainLayout, visible]);

  const linkLines = useMemo(() => {
    if (!links?.length) return [];
    const at = new Map(visible.filter((p) => p.point.id).map((p) => [p.point.id as string, p]));
    return links.flatMap((l) => {
      const members = l.project_ids.map((id) => at.get(id)).filter((p): p is Placed => !!p);
      const lines: { key: string; x1: number; y1: number; x2: number; y2: number }[] = [];
      for (let i = 0; i + 1 < members.length; i++) {
        const a = members[i];
        const b = members[i + 1];
        lines.push({ key: `${l.id}-${i}`, x1: a.x, y1: a.y, x2: b.x, y2: b.y });
      }
      return lines;
    });
  }, [links, visible]);

  const readableVisible = useMemo(() => visible.filter((p) => p.point.id).length, [visible]);
  const allLabels = readableVisible <= LABEL_LIMIT;
  const handleBackground = useCallback(() => onSelect?.(null), [onSelect]);

  const discLabel = (d: Disc, color: string) => {
    const above = d.cy - d.r - 8 > 12;
    return (
      <text
        x={d.cx}
        y={above ? d.cy - d.r - 8 : d.cy + d.r + 16}
        textAnchor="middle"
        fontSize={compact ? 8 : 11}
        fontWeight={700}
        letterSpacing="0.08em"
        style={{ fill: color, ...LABEL_HALO }}
      >
        {truncate(d.label, compact ? 22 : 40).toUpperCase()}
      </text>
    );
  };

  return (
    <div ref={wrapRef} className={className ?? 'relative h-full w-full overflow-hidden'}>
      {w > 0 && h > 0 && (
        <svg
          width={w}
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          className="block select-none"
          onClick={handleBackground}
          role="img"
          aria-label={t('portfolio.map.ariaLabel')}
        >
          <defs>
            <pattern id={gridId} width={compact ? 20 : 40} height={compact ? 20 : 40} patternUnits="userSpaceOnUse">
              <path
                d={compact ? 'M 20 0 L 0 0 0 20' : 'M 40 0 L 0 0 0 40'}
                fill="none"
                style={{ stroke: 'hsl(var(--border))' }}
                strokeWidth={1}
              />
            </pattern>
          </defs>
          <rect width={w} height={h} fill={`url(#${gridId})`} />

          {domainDiscs.map((d) => (
            <g key={`domain-${d.key}`}>
              <circle cx={d.cx} cy={d.cy} r={d.r} style={{ fill: MAP_COLORS.motif, fillOpacity: 0.06 }} />
              {discLabel(d, MAP_COLORS.motif)}
            </g>
          ))}

          {motifDiscs.map((d) => (
            <g key={`motif-${d.key}`}>
              <circle
                cx={d.cx}
                cy={d.cy}
                r={d.r}
                strokeDasharray="5 5"
                strokeWidth={1.3}
                style={{ fill: MAP_COLORS.motif, fillOpacity: 0.04, stroke: MAP_COLORS.motif, strokeOpacity: 0.7 }}
              />
              {discLabel(d, MAP_COLORS.motif)}
            </g>
          ))}

          {linkLines.map((l) => (
            <line
              key={l.key}
              x1={l.x1}
              y1={l.y1}
              x2={l.x2}
              y2={l.y2}
              stroke={MAP_COLORS.suggestion}
              strokeWidth={1.4}
              strokeOpacity={0.8}
            />
          ))}

          {shown.map((p) => {
            const id = p.point.id;
            const selected = id !== null && id === selectedId;
            const highlighted = id !== null && !!highlightIds?.has(id);
            return (
              <Dot
                key={p.key}
                id={id}
                title={p.point.title}
                meta={[p.point.domain_name, p.point.published ? t('portfolio.selection.it.published') : '']
                  .filter(Boolean)
                  .join(' · ')}
                x={p.x}
                y={p.y}
                visible={isVisible(p)}
                selected={selected}
                highlighted={highlighted}
                qualified={p.point.status === 'qualified'}
                suggested={id !== null && !!suggestedIds?.has(id)}
                labelShown={allLabels || selected || highlighted}
                compact={compact}
                onSelect={compact ? undefined : onSelect}
              />
            );
          })}
        </svg>
      )}

      {!compact && w > 0 && shown.length > 0 && visible.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <p className="rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground">
            {t('portfolio.map.noneAtDate')}
          </p>
        </div>
      )}
    </div>
  );
};

export default SemanticMap;
